// Proxies tried in order. Each defines how to build the request URL and
// how to extract the HTML string from the response.
// The Cloud Function is tried first — it works for any site including
// AllRecipes. Public proxies are fallbacks for when the function is unavailable.
const PROXIES = [
  {
    // Firebase Cloud Function — server-side fetch with real browser headers
    build:   url => `https://us-central1-meal-planner-2966f.cloudfunctions.net/fetchRecipe?url=${encodeURIComponent(url)}`,
    extract: res => res.text(),
  },
  {
    // Returns raw HTML directly — works for most sites except AllRecipes
    build:   url => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
    extract: res => res.text(),
  },
  {
    // Returns JSON: { contents: "<html>..." }
    build:   url => `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
    extract: async res => { const d = await res.json(); return d.contents || ""; },
  },
];

// Cross-browser compatible fetch with timeout (AbortSignal.timeout not in Safari 15)
function fetchWithTimeout(url, ms = 20000){
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
}

// Common cooking units for ingredient parsing
const UNITS = new Set([
  "cup","cups","c",
  "tablespoon","tablespoons","tbsp","tbs","tb",
  "teaspoon","teaspoons","tsp","ts",
  "ounce","ounces","oz",
  "pound","pounds","lb","lbs",
  "gram","grams","g",
  "kilogram","kilograms","kg",
  "milliliter","milliliters","ml",
  "liter","liters","l",
  "pint","pints","pt",
  "quart","quarts","qt",
  "gallon","gallons","gal",
  "clove","cloves",
  "can","cans",
  "slice","slices",
  "bunch","bunches",
  "pinch","pinches",
  "dash","dashes",
  "handful","handfuls",
  "piece","pieces",
  "sprig","sprigs",
  "stalk","stalks",
  "head","heads",
  "package","packages","pkg",
]);

function parseFraction(str){
  str = str.trim()
    .replace("½","1/2").replace("⅓","1/3").replace("⅔","2/3")
    .replace("¼","1/4").replace("¾","3/4")
    .replace("⅛","1/8").replace("⅜","3/8").replace("⅝","5/8").replace("⅞","7/8");
  const mixed = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if(mixed) return parseFloat(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
  const frac = str.match(/^(\d+)\/(\d+)$/);
  if(frac) return parseInt(frac[1]) / parseInt(frac[2]);
  const num = parseFloat(str);
  return isNaN(num) ? null : num;
}

function parseIngredientString(raw){
  // Strip HTML tags
  const str = raw.replace(/<[^>]+>/g, "").trim();
  if(!str) return null;

  // Extract leading quantity (digits, fractions, unicode vulgar fractions)
  const qtyMatch = str.match(/^([0-9½⅓⅔¼¾⅛⅜⅝⅞\s/\-–.]+)/);
  let quantity = "";
  let rest = str;

  if(qtyMatch){
    const raw = qtyMatch[1].trim();
    const num = parseFraction(raw);
    if(num !== null){
      quantity = num % 1 === 0 ? String(Math.round(num)) : String(Math.round(num * 100) / 100);
      rest = str.slice(qtyMatch[0].length).trim();
    }
  }

  // Match unit as first word
  const words = rest.split(/\s+/);
  let unit = "";
  let nameStart = 0;

  if(words.length > 0){
    const candidate = words[0].toLowerCase().replace(/[.,()]/g, "");
    if(UNITS.has(candidate)){
      unit = words[0].replace(/[.,]/g, "");
      nameStart = 1;
    }
  }

  const name = words.slice(nameStart).join(" ").replace(/^[,\s]+/, "").trim();

  return { name: name || str, quantity, unit, section: "Other" };
}

function parseInstructions(instructions){
  if(!instructions) return "";
  if(typeof instructions === "string") return instructions.trim();

  if(Array.isArray(instructions)){
    return instructions.map((item, i) => {
      const n = i + 1;
      if(typeof item === "string") return `${n}. ${item.trim()}`;
      if(item["@type"] === "HowToSection"){
        const sectionName = item.name ? `\n${item.name}\n` : "";
        const steps = (item.itemListElement || []).map((s, j) =>
          `${j + 1}. ${typeof s === "string" ? s : s.text || ""}`
        ).join("\n");
        return sectionName + steps;
      }
      if(item.text) return `${n}. ${item.text.trim()}`;
      return "";
    }).filter(Boolean).join("\n");
  }

  return "";
}

function findRecipe(json){
  if(!json || typeof json !== "object") return null;

  // Direct match
  const type = json["@type"];
  if(type === "Recipe" || (Array.isArray(type) && type.includes("Recipe"))) return json;

  // @graph array
  if(Array.isArray(json["@graph"])){
    for(const item of json["@graph"]){
      const found = findRecipe(item);
      if(found) return found;
    }
  }

  // Root-level array (some sites wrap in [])
  if(Array.isArray(json)){
    for(const item of json){
      const found = findRecipe(item);
      if(found) return found;
    }
  }

  return null;
}

export async function importRecipeFromUrl(url){
  let html = "";

  for(const proxy of PROXIES){
    try{
      const res = await fetchWithTimeout(proxy.build(url));
      if(!res.ok) continue;
      const text = await proxy.extract(res);
      if(text && text.includes("application/ld+json")){ html = text; break; }
    } catch {
      continue;
    }
  }

  if(!html) throw new Error("Could not fetch the page. Check the URL and try again.");

  const doc = new DOMParser().parseFromString(html, "text/html");

  const scripts = doc.querySelectorAll('script[type="application/ld+json"]');

  for(const script of scripts){
    try{
      const json = JSON.parse(script.textContent);
      const recipe = findRecipe(json);
      if(!recipe) continue;

      // Servings
      const rawYield = recipe.recipeYield;
      let servings = "";
      if(rawYield){
        const s = Array.isArray(rawYield) ? rawYield[0] : rawYield;
        const m = String(s).match(/\d+/);
        if(m) servings = m[0];
      }

      // Ingredients
      const ingredients = (recipe.recipeIngredient || [])
        .map(parseIngredientString)
        .filter(Boolean)
        .filter(i => i.name);

      // Instructions → notes
      const notes = parseInstructions(recipe.recipeInstructions);

      // Source attribution
      const sourceName = doc.querySelector('meta[property="og:site_name"]')?.content
        || new URL(url).hostname.replace("www.","");

      return {
        name:        recipe.name || "",
        servings,
        ingredients: ingredients.length ? ingredients : [{ name:"", quantity:"", unit:"", section:"Other" }],
        notes:       notes ? `${notes}\n\nSource: ${url}` : `Source: ${url}`,
        sourceName,
      };
    } catch {
      continue;
    }
  }

  throw new Error(
    "No recipe data found on this page. The site may not support automatic import."
  );
}
